import { Controller } from 'stimulus'

import cookies from 'services/cookies'
import { includes, forEach, find } from 'utils'
import { attr } from 'utils/dom'

let Masonry = null

export default class extends Controller {
  static targets = ['link', 'image', 'masonry', 'card']

  connect () {
    this.listName = attr('data-list-name', this.element)
    this.queryId = attr('data-query-id', this.element)

    this.removeLoadedPlaceholders()
    this.refreshMasonry()
    this.pushDataLayerImpressions()
  }

  disconnect () {
    if (this._masonry) {
      this._masonry.destroy()
    }
  }

  pushDataLayerImpressions () {
    if (this.linkTargets.length === 0) return

    dataLayer.push({ ecommerce: null })
    dataLayer.push({
      event: 'ce_impression',
      ecommerce: {
        currencyCode: 'EUR',
        impressions: this.linkTargets.slice(0, 10).map(e => this.getDataLayerAttributes(e))
      }
    })
  }

  pushDataLayerClick (e) {
    const card = find((card) => card.contains(e.currentTarget), this.cardTargets)

    const dataLayerPayloadJSON = attr('data-datalayer', e.currentTarget)
    const dataLayerPayload = this.parseData(dataLayerPayloadJSON)

    this.pushGTMProductClickEvent(card, dataLayerPayload)
    this.pushSearchProductClickEvent(card)
  }

  pushGTMProductClickEvent (card, data) {
    if (!data) { return }

    data.list = this.listName
    data.position = this.cardTargets.indexOf(card) + 1

    dataLayer.push({ ecommerce: null })
    dataLayer.push({
      event: 'ce_productclick',
      ecommerce: {
        click: {
          actionField: { list: data.list },
          products: [data]
        }
      }
    })
  }

  pushSearchProductClickEvent (card) {
    if (!this.queryId) { return }

    dataLayer.push({ algolia: null })
    dataLayer.push({
      event: 'plp_product_click_after_search',
      algolia: {
        click: {
          eventName: 'PLP; Product click after search',
          position: this.cardTargets.indexOf(card) + 1,
          objectIDs: [attr('data-item-id', card)],
          queryID: this.queryId,
          userToken: cookies.signed['uuid'] // eslint-disable-line
        }
      }
    })
  }

  parseData (data) {
    if (!data) return
    return JSON.parse(data)
  }

  imageLoaded (e) {
    if (e && e.target) {
      e.target.classList.remove('placeholder')
    }

    if (this.imageTargets.some(image => !image.complete)) { return }

    this.removePlaceholders()
  }

  loadMore (e) {
    const link = e.currentTarget
    link.classList.add('disabled')
    link.setAttribute('aria-disabled', true)
  }

  masonryTargetConnected () {
    this.refreshMasonry()
  }

  get masonry () {
    return this._masonry
  }

  async refreshMasonry () {
    if (!Masonry) {
      Masonry = (await import('masonry-layout')).default
    }

    if (this.previousMasonryTargets) {
      forEach((el) => {
        if (includes(el, this.previousMasonryTargets)) { return }
        this.masonry.appended(el)
      }, this.masonryTargets)
    }

    if (!this._masonry) {
      this._masonry = new Masonry(this.element, {
        itemSelector: '.item-card, .item-card-pagination',
        columnWidth: '.item-sizer',
        percentPosition: true,
        transitionDuration: 0
      })
    } else {
      this._masonry.layout()
    }

    this.previousMasonryTargets = this.masonryTargets
  }

  removeLoadedPlaceholders () {
    this.imageTargets.forEach((image) => {
      if (image.complete) {
        image.classList.remove('placeholder')
      }
    })
  }

  removePlaceholders () {
    this.element.querySelectorAll('.placeholder').forEach((element) => {
      element.classList.remove('placeholder')
    })
  }

  getDataLayerAttributes (element) {
    return JSON.parse(element.getAttribute('data-datalayer'))
  }
}
